import { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { companiesApi } from '@/api/companies.api';
import { useMasterData } from '@/hooks/useMasterData';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column, TableRowActionLink, TableRowActions } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { MasterLocaleSelects } from '@/components/master/MasterLocaleSelects';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { DepartmentAccessModal } from '@/components/company/DepartmentAccessModal';
import { HrHolidayTypesSection } from '@/modules/hr/components/HrHolidayTypesSection';
import { HrWeekPatternsSection } from '@/modules/hr/components/HrWeekPatternsSection';
import { useDebounce } from '@/hooks/useDebounce';
import { usePageAccess } from '@/hooks/usePageAccess';
import { usePagination } from '@/hooks/usePagination';
import type { CompanyRequest, CompanyResponse } from '@/types/company';
import type { HrWeekPattern } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

const empty: CompanyRequest = {
  name: '',
  code: '',
  countryCode: 'IN',
  currencyCode: 'INR',
  timezone: 'Asia/Kolkata',
};

export function CompaniesPage() {
  const { canCreate } = usePageAccess();
  const { loading: metaLoading } = useMasterData();
  const pagination = usePagination();
  const debouncedSearch = useDebounce(pagination.search);
  const [data, setData] = useState<CompanyResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const [accessCompany, setAccessCompany] = useState<CompanyResponse | null>(null);
  const [editing, setEditing] = useState<CompanyResponse | null>(null);
  const [form, setForm] = useState<CompanyRequest>(empty);
  const [calendarCompany, setCalendarCompany] = useState<CompanyResponse | null>(null);
  const [weekPatterns, setWeekPatterns] = useState<HrWeekPattern[]>([]);
  const [selectedPatternId, setSelectedPatternId] = useState<number | ''>('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await companiesApi.list({
        page: pagination.page,
        size: pagination.size,
        search: debouncedSearch || undefined,
      });
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, debouncedSearch, pagination]);

  useEffect(() => {
    load();
  }, [load]);

  const columns: Column<CompanyResponse>[] = [
    { key: 'code', header: 'Code' },
    { key: 'name', header: 'Name' },
    { key: 'countryCode', header: 'Country' },
    { key: 'currencyCode', header: 'Currency' },
    { key: 'timezone', header: 'Timezone' },
    {
      key: 'isDefault',
      header: 'Default',
      render: (r) => (r.isDefault ? 'Yes' : 'No'),
    },
    {
      key: 'actions',
      header: 'Actions',
      actions: true,
      render: (r) => (
        <TableRowActions>
          <TableRowActionLink
            onClick={() => {
              setEditing(r);
              setForm({
                name: r.name,
                code: r.code,
                taxRegistrationNumber: r.taxRegistrationNumber,
                registrationNumber: r.registrationNumber,
                logoUrl: r.logoUrl,
                addressLine1: r.addressLine1,
                city: r.city,
                email: r.email,
                countryCode: r.countryCode,
                currencyCode: r.currencyCode,
                timezone: r.timezone,
              });
              setModalOpen(true);
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: 14 }} />
            Edit
          </TableRowActionLink>
          <TableRowActionLink
            onClick={async () => {
              setCalendarCompany(r);
              try {
                const [cal, patterns] = await Promise.all([
                  companiesApi.getWorkCalendar(r.id),
                  companiesApi.listWeekPatterns(r.id),
                ]);
                setWeekPatterns(patterns);
                setSelectedPatternId(cal.weekPatternId ?? patterns.find((p) => p.active)?.id ?? '');
              } catch {
                setWeekPatterns([]);
                setSelectedPatternId('');
              }
              setCalendarOpen(true);
            }}
          >
            <CalendarMonthOutlinedIcon sx={{ fontSize: 14 }} />
            Calendar
          </TableRowActionLink>
          <TableRowActionLink
            title="Department access"
            onClick={() => {
              setAccessCompany(r);
              setAccessOpen(true);
            }}
          >
            <GroupsOutlinedIcon sx={{ fontSize: 14 }} />
            Access
          </TableRowActionLink>
        </TableRowActions>
      ),
    },
  ];

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await companiesApi.update(editing.id, form);
      else await companiesApi.create(form);
      notify.success('Saved');
      setModalOpen(false);
      load();
    } catch (err) {
      showApiError(err, 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveCalendar = async () => {
    if (!calendarCompany || !selectedPatternId) {
      notify.error('Select a week pattern master');
      return;
    }
    setSaving(true);
    try {
      await companiesApi.updateWorkCalendar(calendarCompany.id, {
        weekPatternId: Number(selectedPatternId),
      });
      notify.success('Work calendar updated');
      setCalendarOpen(false);
    } catch (err) {
      showApiError(err, 'Calendar update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Companies"
        description="Manage legal entities, calendars, and fiscal settings per company."
        actions={
          canCreate ? (
            <Button
              onClick={() => {
                setEditing(null);
                setForm(empty);
                setModalOpen(true);
              }}
            >
              <AddIcon fontSize="small" />
              Add company
            </Button>
          ) : undefined
        }
      />

      <div className="mb-4">
        <Input
          placeholder="Search companies…"
          value={pagination.search}
          onChange={(e) => pagination.setSearch(e.target.value)}
        />
      </div>

      <DataTable columns={columns} data={data} loading={loading} keyField="id" />

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit company' : 'New company'}>
        <div className="grid gap-3">
          <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input label="Code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          <Input
            label="Tax registration"
            value={form.taxRegistrationNumber ?? ''}
            onChange={(e) => setForm({ ...form, taxRegistrationNumber: e.target.value })}
          />
          <Input
            label="Registration number"
            value={form.registrationNumber ?? ''}
            onChange={(e) => setForm({ ...form, registrationNumber: e.target.value })}
          />
          <Input label="Email" value={form.email ?? ''} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <MasterLocaleSelects
            value={{
              countryCode: form.countryCode ?? '',
              currencyCode: form.currencyCode ?? '',
              timezone: form.timezone ?? '',
            }}
            onChange={(patch) => setForm({ ...form, ...patch })}
            disabled={metaLoading}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving}>
              Save
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        title={calendarCompany ? `Work calendar — ${calendarCompany.name}` : 'Work calendar'}
        size="xl"
      >
        <div className="grid max-h-[70vh] gap-6 overflow-y-auto pr-1">
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Assign week pattern</h3>
            <p className="text-sm text-slate-500">
              Choose a pattern from your company master, or create one below.
            </p>
            <Select
              label="Week pattern master"
              value={selectedPatternId === '' ? '' : String(selectedPatternId)}
              options={[
                { value: '', label: weekPatterns.length ? 'Select pattern…' : 'No patterns — create one below' },
                ...weekPatterns.filter((p) => p.active).map((p) => ({
                  value: String(p.id),
                  label: `${p.name} (${p.code})`,
                })),
              ]}
              onChange={(v) => setSelectedPatternId(v ? Number(v) : '')}
            />
            <div className="flex justify-end gap-2">
              <Button onClick={saveCalendar} disabled={saving || !selectedPatternId} loading={saving}>
                Assign to work calendar
              </Button>
            </div>
          </section>

          {calendarCompany ? (
            <section className="border-t border-slate-200 pt-6 dark:border-slate-700">
              <h3 className="mb-1 text-sm font-semibold text-slate-800 dark:text-slate-100">Week pattern master</h3>
              <p className="mb-4 text-sm text-slate-500">
                Define working days and default hours. Reusable across schedules and attendance.
              </p>
              <HrWeekPatternsSection
                companyId={calendarCompany.id}
                compact
                inlineActions
                selectedId={selectedPatternId === '' ? null : Number(selectedPatternId)}
                onPatternsChange={setWeekPatterns}
                onSelect={(p) => setSelectedPatternId(p.id)}
              />
            </section>
          ) : null}

          {calendarCompany ? (
            <section className="border-t border-slate-200 pt-6 dark:border-slate-700">
              <h3 className="mb-1 text-sm font-semibold text-slate-800 dark:text-slate-100">Holiday types</h3>
              <p className="mb-4 text-sm text-slate-500">
                Customize categories for this company. Add calendar dates under HR → Public holidays.
              </p>
              <HrHolidayTypesSection companyId={calendarCompany.id} compact inlineActions />
            </section>
          ) : null}

          <div className="flex justify-end border-t border-slate-200 pt-4 dark:border-slate-700">
            <Button variant="secondary" onClick={() => setCalendarOpen(false)}>
              Close
            </Button>
          </div>
        </div>
      </Modal>

      <DepartmentAccessModal
        company={accessCompany}
        open={accessOpen}
        onClose={() => setAccessOpen(false)}
      />
    </div>
  );
}
